import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Ruler, CheckCircle2, BarChart3, HelpCircle, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTelegram } from '../../hooks/useTelegram'
import { api } from '../../services/api'
import { type CharacterGender } from '../../assets/character'
import { nextMeasurementCycleStart } from '@tfa/shared'

const F = 'Roboto, sans-serif'

export interface MeasurementsValues {
  arms: string
  chest: string
  waist: string
  belly: string
  pelvis: string
  hips: string
  thigh: string
  calf: string
  neck: string
}

const EMPTY_VALUES: MeasurementsValues = {
  arms: '', chest: '', waist: '', belly: '',
  pelvis: '', hips: '', thigh: '', calf: '', neck: '',
}

interface ExistingMeasurement {
  arms?: number | null
  chest?: number | null
  waist?: number | null
  belly?: number | null
  pelvis?: number | null
  hips?: number | null
  thigh?: number | null
  calf?: number | null
  neck?: number | null
}

interface Props {
  open: boolean
  onClose: () => void
  // #526: xpAwarded — фактическое начисление за эту сдачу; поздравление
  // показывает его вместо захардкоженного «+1 XP».
  onSubmitted?: (streak?: { advanced?: boolean; streakDays?: number } | null, opts?: { isEdit?: boolean; xpAwarded?: number }) => void
  /** YYYY-MM-DD; defaults to today if omitted */
  date?: string
  /** Existing measurement record for the selected date (prefills the form) */
  existing?: ExistingMeasurement | null
  /** Most-recently saved value per field across all dates (used as placeholders) */
  lastValues?: Partial<Record<keyof MeasurementsValues, number>>
  /**
   * YYYY-MM-DD дня текущего цикла замеров (Сб→Пт), на котором уже есть запись.
   * Если задан и не равен `date` — модалка показывает locked-вью вместо
   * формы: «Замеры на этой неделе уже сданы», кнопка переключиться на тот
   * день. Один Measurement на цикл; окно сдачи — Сб/Вс/Пн (#509).
   */
  weeklyLockedAt?: string | null
  /** Колбэк «открыть тот день в дневнике»; если не задан, кнопка скрывается. */
  onOpenLockedDay?: (date: string) => void
  /** CLUB-product re-tone: teal-green ruler + save button. */
  isClub?: boolean
}

// Гайд «как делать замер» — открывается по «?» рядом с каждым полем.
// Картинку-схему пока рисуем inline-SVG (силуэт + цветная линия в зоне замера).
// Текст согласован с фитнес-методичкой ТФА (4 шага + советы).
interface MeasurementGuide {
  title: string
  subtitle: string
  steps: { title: string; text: string }[]
  tips: string[]
}
const MEASUREMENT_GUIDES: Record<keyof MeasurementsValues, MeasurementGuide> = {
  arms: {
    title: 'Руки (бицепс)',
    subtitle: 'Как правильно измерить руку',
    steps: [
      { title: 'Найдите точку измерения', text: 'Самая широкая часть бицепса при напряжённой руке (рука согнута в локте, кулак к плечу).' },
      { title: 'Оберните сантиметр', text: 'Параллельно полу, перпендикулярно оси руки. Без натяга.' },
      { title: 'Держите правильно', text: 'Сантиметр плотно прилегает к коже, но не вдавливается.' },
      { title: 'Сделайте замер', text: 'Зафиксируйте результат в момент пикового напряжения мышцы.' },
    ],
    tips: ['Меряйте обе руки и записывайте бо́льшую', 'Без рывков — равномерное напряжение'],
  },
  chest: {
    title: 'Грудь',
    subtitle: 'Как правильно измерить обхват груди',
    steps: [
      { title: 'Найдите точку измерения', text: 'У женщин — по самой выступающей точке груди. У мужчин — на уровне сосков.' },
      { title: 'Оберните сантиметр', text: 'Горизонтально вокруг корпуса, параллельно полу.' },
      { title: 'Держите правильно', text: 'Руки опущены и расслаблены, плечи в естественном положении.' },
      { title: 'Сделайте замер', text: 'На выдохе, без втягивания груди и без напряжения мышц.' },
    ],
    tips: ['Не задерживайте дыхание', 'Сантиметр сзади на уровне лопаток'],
  },
  waist: {
    title: 'Талия',
    subtitle: 'Как правильно измерить талию',
    steps: [
      { title: 'Найдите точку измерения', text: 'Самая узкая часть туловища — обычно на 2-3 см выше пупка.' },
      { title: 'Оберните сантиметр', text: 'Мягкий сантиметр вокруг талии параллельно полу.' },
      { title: 'Держите правильно', text: 'Сантиметр плотно прилегает, но не сдавливает кожу.' },
      { title: 'Сделайте замер', text: 'Проверьте, что сантиметр параллелен полу, и запишите результат.' },
    ],
    tips: ['Меряйте на выдохе', 'Не втягивайте живот', 'Проводите замер утром, натощак', 'Повторяйте 2-3 раза для точности'],
  },
  belly: {
    title: 'Живот',
    subtitle: 'Как правильно измерить обхват живота',
    steps: [
      { title: 'Найдите точку измерения', text: 'Через пупок — самая выступающая часть передней брюшной стенки.' },
      { title: 'Оберните сантиметр', text: 'Горизонтально, ровно по пупку, параллельно полу.' },
      { title: 'Держите правильно', text: 'Стойте расслабленно, дыхание спокойное.' },
      { title: 'Сделайте замер', text: 'На выдохе, без втягивания и без выпячивания живота.' },
    ],
    tips: ['Меряйте на выдохе', 'Не задерживайте дыхание', 'Утром перед едой — самые точные значения'],
  },
  pelvis: {
    title: 'Таз',
    subtitle: 'Как правильно измерить обхват таза',
    steps: [
      { title: 'Найдите точку измерения', text: 'Верхняя часть бёдер — по выступающим точкам тазовых костей.' },
      { title: 'Оберните сантиметр', text: 'Параллельно полу, ниже талии примерно на 8-12 см.' },
      { title: 'Держите правильно', text: 'Ноги вместе, вес распределён равномерно.' },
      { title: 'Сделайте замер', text: 'Зафиксируйте обхват в самой широкой точке таза.' },
    ],
    tips: ['Меряйте без одежды или в тонком белье', 'Без втягивания мышц'],
  },
  hips: {
    title: 'Ягодицы / Таз',
    subtitle: 'Как правильно измерить обхват ягодиц и таза',
    steps: [
      { title: 'Найдите точку измерения', text: 'Самая выступающая часть ягодиц.' },
      { title: 'Оберните сантиметр', text: 'Горизонтально вокруг ягодиц, параллельно полу.' },
      { title: 'Держите правильно', text: 'Ноги вместе, прямая спина, расслабленные ягодицы.' },
      { title: 'Сделайте замер', text: 'Сантиметр идёт через самую полную часть и сходится спереди.' },
    ],
    tips: ['Не напрягайте ягодицы', 'Стойте перед зеркалом для контроля'],
  },
  thigh: {
    title: 'Бедро / Нога',
    subtitle: 'Как правильно измерить обхват бедра',
    steps: [
      { title: 'Найдите точку измерения', text: 'Самая широкая часть бедра — сразу под ягодичной складкой.' },
      { title: 'Оберните сантиметр', text: 'Горизонтально вокруг одного бедра, параллельно полу.' },
      { title: 'Держите правильно', text: 'Стойте прямо, вес на обеих ногах, мышцы расслаблены.' },
      { title: 'Сделайте замер', text: 'Замер делайте на одной и той же ноге каждый раз.' },
    ],
    tips: ['Меряйте обе ноги, записывайте бо́льшую', 'Не напрягайте бедро'],
  },
  calf: {
    title: 'Голень',
    subtitle: 'Как правильно измерить обхват голени',
    steps: [
      { title: 'Найдите точку измерения', text: 'Самая широкая часть икроножной мышцы.' },
      { title: 'Оберните сантиметр', text: 'Перпендикулярно оси голени.' },
      { title: 'Держите правильно', text: 'Стойте прямо, вес распределён равномерно.' },
      { title: 'Сделайте замер', text: 'Без напряжения и без вставания на носки.' },
    ],
    tips: ['Меряйте обе ноги', 'Записывайте бо́льшее значение'],
  },
  neck: {
    title: 'Шея',
    subtitle: 'Как правильно измерить обхват шеи',
    steps: [
      { title: 'Найдите точку измерения', text: 'Чуть ниже кадыка, в самой узкой части шеи.' },
      { title: 'Оберните сантиметр', text: 'Горизонтально, параллельно полу.' },
      { title: 'Держите правильно', text: 'Голову держите прямо, взгляд вперёд.' },
      { title: 'Сделайте замер', text: 'Сантиметр плотно прилегает, не сдавливая горло.' },
    ],
    tips: ['Подбородок параллельно полу', 'Не наклоняйте голову'],
  },
}

// Диапазоны должны совпадать с zod-схемой /measurements в packages/api/src/routes/diary.ts
const FIELDS: { key: keyof MeasurementsValues; label: string; unit: string; placeholder: string; color: string; min: number; max: number }[] = [
  { key: 'arms', label: 'Руки', unit: 'см', placeholder: '35', color: '#4A9FE5', min: 10, max: 100 },
  { key: 'chest', label: 'Грудь', unit: 'см', placeholder: '100', color: '#6C63FF', min: 20, max: 300 },
  { key: 'waist', label: 'Талия', unit: 'см', placeholder: '80', color: '#FF9500', min: 20, max: 300 },
  { key: 'belly', label: 'Живот', unit: 'см', placeholder: '85', color: '#F97316', min: 20, max: 300 },
  // «Таз» убран; объём ягодиц/таза теперь меряется полем hips («Ягодицы / Таз»).
  { key: 'hips', label: 'Ягодицы / Таз', unit: 'см', placeholder: '100', color: '#EC4899', min: 20, max: 300 },
  { key: 'thigh', label: 'Бедро / Нога', unit: 'см', placeholder: '55', color: '#FF2D55', min: 10, max: 150 },
  { key: 'calf', label: 'Голень', unit: 'см', placeholder: '38', color: '#24A1DE', min: 10, max: 100 },
  { key: 'neck', label: 'Шея', unit: 'см', placeholder: '38', color: '#8B7FF5', min: 15, max: 80 },
]

// Разрешаем только цифры и одну точку/запятую (запятая нормализуется в точку).
// Максимум 3 знака до разделителя и 1 знак после — реалистично для см.
// Иконки полей и картинки-гайды по «?» — реалистичные фото частей тела, раздельно
// по полу (public/measure-icons и public/measure-guide). Иконка — фото без стрелки,
// картинка в гайде — фото со стрелкой-замером.
const ASSET_BASE = import.meta.env.BASE_URL
function measureSex(gender: CharacterGender): 'male' | 'female' {
  return gender === 'FEMALE' ? 'female' : 'male'
}
function measureIconUrl(key: keyof MeasurementsValues, gender: CharacterGender): string {
  return `${ASSET_BASE}measure-icons/${key}-${measureSex(gender)}.png`
}
function measureGuideUrl(key: keyof MeasurementsValues, gender: CharacterGender): string {
  return `${ASSET_BASE}measure-guide/${key}-${measureSex(gender)}.png`
}

function sanitizeMeasurementInput(raw: string): string {
  const normalized = raw.replace(',', '.').replace(/[^\d.]/g, '')
  const firstDot = normalized.indexOf('.')
  const cleaned = firstDot === -1
    ? normalized
    : normalized.slice(0, firstDot + 1) + normalized.slice(firstDot + 1).replace(/\./g, '')
  const [intPart = '', decPart = ''] = cleaned.split('.')
  const trimmedInt = intPart.slice(0, 3)
  const trimmedDec = decPart.slice(0, 1)
  if (cleaned.includes('.')) return `${trimmedInt}.${trimmedDec}`
  return trimmedInt
}

export function MeasurementsSheet({ open, onClose, onSubmitted, date, existing, lastValues, weeklyLockedAt, onOpenLockedDay, isClub = false }: Props) {
  const navigate = useNavigate()
  const [vals, setVals] = useState<MeasurementsValues>(EMPTY_VALUES)
  // Открытый гайд по конкретному замеру (null = закрыт).
  const [activeGuide, setActiveGuide] = useState<keyof MeasurementsValues | null>(null)
  const queryClient = useQueryClient()
  const { notificationHaptic } = useTelegram()
  const isWeeklyLocked = !!weeklyLockedAt && weeklyLockedAt !== date
  // Пол клиента — для выбора фигуры-маскота в гайде «как измерять» (#замеры-фигура).
  // Читаем из кэша ['user','me'] (заполнен до открытия шторки), без доп. запроса.
  const gender = ((queryClient.getQueryData(['user', 'me']) as { gender?: string } | undefined)?.gender ?? null) as CharacterGender

  useEffect(() => {
    if (!open) return
    if (existing) {
      const next: MeasurementsValues = { ...EMPTY_VALUES }
      for (const f of FIELDS) {
        const v = existing[f.key]
        next[f.key] = v != null ? String(v) : ''
      }
      setVals(next)
    } else {
      setVals(EMPTY_VALUES)
    }
  }, [open, existing])

  const errors = useMemo<Partial<Record<keyof MeasurementsValues, string>>>(() => {
    const out: Partial<Record<keyof MeasurementsValues, string>> = {}
    for (const f of FIELDS) {
      const raw = vals[f.key].trim()
      if (raw === '') continue
      const v = parseFloat(raw)
      if (!Number.isFinite(v)) {
        out[f.key] = 'Введите число'
        continue
      }
      if (v < f.min || v > f.max) {
        out[f.key] = `${f.label}: допустимо ${f.min}–${f.max} ${f.unit}`
      }
    }
    return out
  }, [vals])

  const errorList = useMemo(() => Object.values(errors).filter(Boolean) as string[], [errors])
  const hasErrors = errorList.length > 0

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Parameters<typeof api.addMeasurement>[0] = (() => {
        const today = new Date()
        const dateKey = date ?? `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        const result: Parameters<typeof api.addMeasurement>[0] = { date: dateKey }
        for (const f of FIELDS) {
          const v = parseFloat(vals[f.key])
          if (!isNaN(v) && v > 0) result[f.key] = v
        }
        return result
      })()
      return api.addMeasurement(payload)
    },
    onSuccess: (data: { streak?: { advanced?: boolean; streakDays?: number } | null; xpAwarded?: number }) => {
      notificationHaptic('success')
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // Повторная сдача в открытом окне = редактирование: запись на этот день
      // уже содержала окружности. В этом случае показываем не праздничное
      // поздравление с «наградой», а тихое подтверждение «изменения сохранены».
      const isEdit = !!existing && FIELDS.some((f) => typeof existing[f.key] === 'number' && (existing[f.key] as number) > 0)
      onClose()
      onSubmitted?.(data?.streak ?? null, { isEdit, xpAwarded: data?.xpAwarded })
    },
    onError: (err: unknown) => {
      notificationHaptic('error')
      // Достаём конкретную причину из 400 Validation Error API,
      // чтобы пользователь видел, какое поле вне допустимого диапазона.
      const anyErr = err as { response?: { data?: { details?: Array<{ field?: string; message?: string }> } } }
      const details = anyErr?.response?.data?.details
      if (details && details.length > 0) {
        const first = details[0]
        const field = FIELDS.find((f) => f.key === first.field)
        if (field) {
          toast.error(`${field.label}: реалистичное значение ${field.min}–${field.max} ${field.unit}`)
          return
        }
        if (first.message) {
          toast.error(first.message)
          return
        }
      }
      toast.error('Не удалось сохранить замеры')
    },
  })

  const hasAnyValue = Object.values(vals).some((v) => v.trim() !== '')
  const canSave = hasAnyValue && !hasErrors && !saveMutation.isPending

  const handleSave = () => {
    if (!hasAnyValue) return
    if (hasErrors) {
      notificationHaptic('error')
      toast.error(errorList[0])
      return
    }
    saveMutation.mutate()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 400,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-measurements"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(30px)',
              borderTopLeftRadius: 26, borderTopRightRadius: 26,
              maxHeight: '88vh', overflowY: 'auto',
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              position: 'relative', // нужно для overlay-гайда (position:absolute inset:0)
            }}
          >
            {/* Handle */}
            <SheetGrip onClose={onClose} />

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 8px' }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C2D41', margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isClub ? (
                    <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(0,184,148,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Ruler size={18} color="#00B894" />
                    </span>
                  ) : (
                    <Ruler size={20} color="#FF9500" />
                  )}
                  Замеры тела
                </h2>
                <p style={{ fontSize: 13, color: '#9AA5B4', margin: 0 }}>
                  {isWeeklyLocked ? 'Раз в неделю — следующая с субботы' : '8 окружностей (см)'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  data-testid="btn-measurements-progress"
                  onClick={() => { onClose(); navigate('/analytics?tab=measurements') }}
                  style={{
                    height: 32, padding: '0 10px', borderRadius: 16,
                    background: 'rgba(74,108,247,0.10)', border: '1px solid rgba(74,108,247,0.25)',
                    color: '#4A6CF7', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                >
                  <BarChart3 size={13} /> Прогресс
                </button>
                <button
                  data-testid="btn-close-measurements"
                  onClick={onClose}
                  style={{
                    background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%',
                    width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={16} color="#6B7B8D" />
                </button>
              </div>
            </div>

            {isWeeklyLocked && weeklyLockedAt ? (
              <LockedView
                lockedAt={weeklyLockedAt}
                onOpenLockedDay={onOpenLockedDay}
                onClose={onClose}
              />
            ) : (
              <>
            {/* Fields */}
            <div style={{ padding: '8px 20px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FIELDS.map((f) => {
                const fieldError = errors[f.key]
                const borderColor = fieldError
                  ? '#FF3B30'
                  : vals[f.key]
                    ? f.color + '66'
                    : f.color + '22'
                return (
                  <div
                    key={f.key}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 4,
                      borderRadius: 14, padding: '10px 12px',
                      background: fieldError ? 'rgba(255,59,48,0.06)' : `${f.color}08`,
                      border: `1px solid ${fieldError ? 'rgba(255,59,48,0.25)' : f.color + '22'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 10,
                        background: `${f.color}18`, overflow: 'hidden', flexShrink: 0,
                      }}>
                        <img
                          src={measureIconUrl(f.key, gender)}
                          alt=""
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#1C2D41', minWidth: 54, flexShrink: 0 }}>
                        {f.label}
                      </span>
                      <input
                        data-testid={`input-measurement-${f.key}`}
                        value={vals[f.key]}
                        onChange={(e) => setVals((prev) => ({ ...prev, [f.key]: sanitizeMeasurementInput(e.target.value) }))}
                        inputMode="decimal"
                        pattern="[0-9]*"
                        placeholder={lastValues?.[f.key] != null ? String(lastValues[f.key]) : f.placeholder}
                        aria-invalid={!!fieldError}
                        style={{
                          flex: 1, height: 38, borderRadius: 10,
                          border: `1.5px solid ${borderColor}`,
                          background: 'rgba(255,255,255,0.85)',
                          padding: '0 10px', fontSize: 16, fontWeight: 700,
                          color: '#1C2D41', outline: 'none', fontFamily: F,
                          transition: 'border-color 0.15s',
                        }}
                      />
                      <span style={{ fontSize: 13, color: '#9AA5B4', fontWeight: 500, flexShrink: 0, minWidth: 22 }}>
                        {f.unit}
                      </span>
                      <button
                        type="button"
                        onClick={() => setActiveGuide(f.key)}
                        aria-label={`Как мерить: ${f.label}`}
                        title={`Как мерить: ${f.label}`}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          padding: 4, color: f.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, opacity: 0.7,
                        }}
                      >
                        <HelpCircle size={18} />
                      </button>
                    </div>
                    {fieldError && (
                      <div
                        data-testid={`error-measurement-${f.key}`}
                        style={{ fontSize: 12, fontWeight: 500, color: '#FF3B30', paddingLeft: 44 }}
                      >
                        {fieldError}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Save button */}
            <div style={{ padding: '0 20px' }}>
              <button
                data-testid="btn-save-measurements"
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  width: '100%', height: 52, borderRadius: 16, border: 'none',
                  background: canSave ? (isClub ? 'linear-gradient(135deg,#00B894,#55EFC4)' : 'linear-gradient(135deg,#1A3A5C,#4A6CF7)') : 'rgba(26,58,92,0.12)',
                  color: canSave ? 'white' : '#9AA5B4',
                  fontSize: 16, fontWeight: 700, cursor: canSave ? 'pointer' : 'default',
                  fontFamily: F, opacity: saveMutation.isPending ? 0.6 : 1,
                  boxShadow: canSave ? (isClub ? '0 6px 20px rgba(0,184,148,0.3)' : '0 6px 20px rgba(74,108,247,0.3)') : 'none',
                }}
              >
                {saveMutation.isPending ? 'Сохранение...' : 'Сохранить замеры'}
              </button>
            </div>
              </>
            )}

            {/* Overlay-гайд «Как делать замер» */}
            {activeGuide && (() => {
              const guide = MEASUREMENT_GUIDES[activeGuide]
              const field = FIELDS.find((f) => f.key === activeGuide)
              if (!guide || !field) return null
              const currentValue = vals[activeGuide]
              return (
                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ duration: 0.18 }}
                  style={{
                    position: 'absolute', inset: 0,
                    background: 'white',
                    display: 'flex', flexDirection: 'column',
                    overflowY: 'auto',
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(26,58,92,0.08)' }}>
                    <button
                      type="button"
                      onClick={() => setActiveGuide(null)}
                      style={{ background: 'rgba(26,58,92,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <ChevronLeft size={18} color="#1C2D41" />
                    </button>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41', margin: 0 }}>Как делать замер</h3>
                    <button
                      type="button"
                      onClick={() => setActiveGuide(null)}
                      style={{ background: 'rgba(26,58,92,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <X size={16} color="#1C2D41" />
                    </button>
                  </div>

                  <div style={{ padding: '16px 20px 28px', flex: 1 }}>
                    {/* Title + current value */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ width: 26, height: 26, borderRadius: 8, overflow: 'hidden', flexShrink: 0, display: 'inline-block', background: `${field.color}18` }}>
                            <img src={measureIconUrl(activeGuide, gender)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </span>
                          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41', margin: 0 }}>{guide.title}</h2>
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7B8D' }}>{guide.subtitle}</div>
                      </div>
                      {currentValue && (
                        <span style={{ fontSize: 12, fontWeight: 700, background: `${field.color}22`, color: field.color, padding: '4px 10px', borderRadius: 10, flexShrink: 0 }}>
                          {currentValue} {field.unit}
                        </span>
                      )}
                    </div>

                    {/* Фото части тела со стрелкой-замером (по полу) */}
                    <div style={{
                      background: `${field.color}10`,
                      borderRadius: 14,
                      padding: '14px 12px',
                      marginBottom: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      minHeight: 140,
                    }}>
                      <img
                        src={measureGuideUrl(activeGuide, gender)}
                        alt={guide.title}
                        style={{ maxHeight: 220, maxWidth: '82%', objectFit: 'contain', borderRadius: 12, display: 'block' }}
                      />
                    </div>

                    {/* Steps */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
                      {guide.steps.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%',
                            background: `${field.color}22`, color: field.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700, flexShrink: 0,
                          }}>{i + 1}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 2 }}>{s.title}</div>
                            <div style={{ fontSize: 12, color: '#6B7B8D', lineHeight: 1.45 }}>{s.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tips */}
                    {guide.tips.length > 0 && (
                      <div style={{
                        background: 'rgba(255,193,7,0.08)',
                        borderRadius: 12,
                        padding: '10px 12px',
                        border: '1px solid rgba(255,193,7,0.18)',
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          💡 Советы
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#5A6B7E', lineHeight: 1.6 }}>
                          {guide.tips.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )
            })()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function formatHumanDate(key: string): string {
  // YYYY-MM-DD → DD.MM
  const [, m, d] = key.split('-')
  return `${d}.${m}`
}

function nextWindowOpenHuman(weeklyAt: string): string {
  // #509: следующее открытие окна — суббота нового цикла (окно Сб→Вс→Пн).
  // Считаем в local TZ: дата показывается пользователю, привязка к UTC не нужна.
  const next = nextMeasurementCycleStart(new Date(`${weeklyAt}T00:00:00`))
  return `${String(next.getDate()).padStart(2, '0')}.${String(next.getMonth() + 1).padStart(2, '0')}`
}

interface LockedViewProps {
  lockedAt: string
  onOpenLockedDay?: (date: string) => void
  onClose: () => void
}

function LockedView({ lockedAt, onOpenLockedDay, onClose }: LockedViewProps) {
  return (
    <div
      data-testid="measurements-weekly-locked"
      style={{ padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div
        style={{
          borderRadius: 18,
          padding: '18px 16px',
          background: 'linear-gradient(135deg, rgba(52,199,89,0.10), rgba(52,199,89,0.04))',
          border: '1px solid rgba(52,199,89,0.25)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <CheckCircle2 size={28} color="#1C9E44" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41' }}>
            Замеры на этой неделе уже сданы
          </span>
          <span style={{ fontSize: 13, color: '#52606D', lineHeight: 1.4 }}>
            Запись от {formatHumanDate(lockedAt)} засчитана за всю неделю.
            Следующая сдача — с {nextWindowOpenHuman(lockedAt)} (суббота).
          </span>
        </div>
      </div>

      {onOpenLockedDay && (
        <button
          data-testid="btn-open-weekly-measurement-day"
          onClick={() => {
            onOpenLockedDay(lockedAt)
            onClose()
          }}
          style={{
            width: '100%', height: 48, borderRadius: 14, border: 'none',
            background: 'rgba(74,108,247,0.10)',
            color: '#4A6CF7',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            fontFamily: F,
          }}
        >
          Открыть {formatHumanDate(lockedAt)} — посмотреть значения
        </button>
      )}

      <button
        onClick={onClose}
        style={{
          width: '100%', height: 48, borderRadius: 14, border: 'none',
          background: 'rgba(0,0,0,0.05)',
          color: '#6B7B8D',
          fontSize: 14, fontWeight: 600, cursor: 'pointer',
          fontFamily: F,
        }}
      >
        Понятно
      </button>
    </div>
  )
}
